import * as React from 'react';
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ItemTypeDisplay } from '@/components/ItemType/ItemTypeDisplay';
import { ContainerTypeTable } from '@/components/ContainerTypeTable';
import {
  Sparkles,
  Download,
  ListFilterIcon,
  CalendarIcon,
  ChevronDownIcon,
} from 'lucide-react';
import { downloadJsonDataAsExcelFile } from '@/utils/download';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsMobile } from '@/hooks/use-mobile';
import { useGetEventFulfillmentDataByFulfillmentStepQuery } from '@/graphql/queries/getEventFulfillmentDataByFulfillmentStep.generated';
import { FilterBar, type FilterDefinition } from '@/components/FilterBar';
import { Badge } from '@/components/ui/badge';
import { TablePagination } from '@/components/TablePagination';
import i18n from '@/utils/i18n';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { type DateRange } from 'react-day-picker';
import { Card } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

interface FulfillmentDataEntry {
  container_type_id: number;
  container_type_name: string;
  image_url?: string | null;
  color_hex_code?: string | null;
  packing_unit: number;
  ordered: number; // containers
  delivered: number; // containers
  in_stock: number; // containers
  handed_out: number; // containers
  received_dirty: number; // containers (for cleaning facility)
  received_sealed: number; // containers (for cleaning facility)
  cleaned: number; // containers (for cleaning facility)
}

// Data structure matching GraphQL naming conventions
interface FulfillmentData {
  fulfillment_entries: FulfillmentDataEntry[];
}

interface EventFulfillmentDataProps {
  eventId?: string;
}

export function EventFulfillmentData({ eventId }: EventFulfillmentDataProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('event');
  const [selectedEventAccountId, setSelectedEventAccountId] = useState<
    string | null
  >(null);
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [isFilterBarExpanded, setIsFilterBarExpanded] = useState(false);
  const isMobile = useIsMobile();

  // Fetch data from GraphQL
  const { data, loading: isLoading } =
    useGetEventFulfillmentDataByFulfillmentStepQuery({
      variables: { event_id: eventId || '' },
      skip: !eventId,
    });

  // Transform GraphQL response to component format
  const fulfillmentData: FulfillmentData = useMemo(() => {
    if (!data?.getEventFulfillmentDataByFulfillmentStep) {
      return { fulfillment_entries: [] };
    }

    const graphqlData = data.getEventFulfillmentDataByFulfillmentStep;
    const { container_types, stock_entries } = graphqlData;

    // Filter stock entries by event account if filter is active
    const filteredStockEntries = selectedEventAccountId
      ? stock_entries.filter(
          (entry) => entry.event_account_id === selectedEventAccountId,
        )
      : stock_entries;

    // Group stock entries by container_type_id and aggregate values
    const stockEntriesByContainerType = new Map<
      number,
      {
        ordered: number;
        delivered: number;
        in_stock: number;
        handed_out: number;
        received_dirty: number;
        received_sealed: number;
        cleaned: number;
      }
    >();

    filteredStockEntries.forEach((entry) => {
      const existing = stockEntriesByContainerType.get(
        entry.container_type_id,
      ) || {
        ordered: 0,
        delivered: 0,
        in_stock: 0,
        handed_out: 0,
        received_dirty: 0,
        received_sealed: 0,
        cleaned: 0,
      };

      stockEntriesByContainerType.set(entry.container_type_id, {
        ordered: existing.ordered + (entry.ordered ?? 0),
        delivered: existing.delivered + entry.delivered,
        in_stock: existing.in_stock + entry.in_stock,
        handed_out: existing.handed_out + entry.handed_out,
        received_dirty: existing.received_dirty + entry.received_dirty,
        received_sealed: existing.received_sealed + entry.received_sealed,
        cleaned: existing.cleaned + entry.cleaned,
      });
    });

    // Create fulfillment entries by combining container types with stock data
    const fulfillment_entries: FulfillmentDataEntry[] = container_types.map(
      (ct) => {
        const stockData = stockEntriesByContainerType.get(ct.id) || {
          ordered: 0,
          delivered: 0,
          in_stock: 0,
          handed_out: 0,
          received_dirty: 0,
          received_sealed: 0,
          cleaned: 0,
        };

        return {
          container_type_id: ct.id,
          container_type_name: ct.name,
          image_url: ct.image_url,
          color_hex_code: ct.color_hex_code,
          packing_unit: ct.packing_unit ?? 1,
          ordered: stockData.ordered,
          delivered: stockData.delivered,
          in_stock: stockData.in_stock,
          handed_out: stockData.handed_out,
          received_dirty: stockData.received_dirty,
          received_sealed: stockData.received_sealed,
          cleaned: stockData.cleaned,
        };
      },
    );

    return { fulfillment_entries };
  }, [data, selectedEventAccountId]);

  // Calculate pagination
  const totalPages = Math.ceil(
    fulfillmentData.fulfillment_entries.length / rowsPerPage,
  );
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedEntries = fulfillmentData.fulfillment_entries.slice(
    startIndex,
    endIndex,
  );

  // Format value with boxes
  const formatValue = (value: number, packingUnit: number): React.ReactNode => {
    if (value === 0) {
      return (
        <>
          <span className="tw:text-neutral-900">--</span>
          <span className="tw:text-neutral-500"> </span>
        </>
      );
    }
    const boxes = Math.floor(value / packingUnit);
    const boxLabel = boxes === 1 ? i18n.t('Common:box') : i18n.t('Event:boxes');
    return (
      <>
        <span className="tw:text-neutral-900">{value}</span>
        <span className="tw:text-neutral-500">
          {' '}
          ({boxes} {boxLabel})
        </span>
      </>
    );
  };

  // Prepare Excel export data
  const excelData = useMemo(() => {
    if (activeTab === 'event') {
      return fulfillmentData.fulfillment_entries.map((entry) => ({
        [i18n.t('Common:containerType')]: entry.container_type_name,
        [i18n.t('Event:ordered')]: entry.ordered,
        [i18n.t('Event:delivered')]: entry.delivered,
        [i18n.t('Event:inStock')]: entry.in_stock,
        [i18n.t('Event:handedOut')]: entry.handed_out,
      }));
    } else {
      return fulfillmentData.fulfillment_entries.map((entry) => ({
        [i18n.t('Common:containerType')]: entry.container_type_name,
        [i18n.t('Event:receivedDirty')]: entry.received_dirty,
        [i18n.t('Event:receivedSealed')]: entry.received_sealed,
        [i18n.t('Event:cleaned')]: entry.cleaned,
      }));
    }
  }, [fulfillmentData, activeTab]);

  const handleDownloadExcel = () => {
    const filename = `fulfillment-data-${eventId || 'all'}`;
    downloadJsonDataAsExcelFile(excelData, filename);
  };

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    setCurrentPage(1); // Reset to first page when switching tabs
  };

  // Get event accounts from data
  const eventAccounts =
    data?.getEventFulfillmentDataByFulfillmentStep?.event_accounts || [];

  // Find selected event account name
  const selectedEventAccount = eventAccounts.find(
    (ea) => ea.id === selectedEventAccountId,
  );

  // Create filter definitions
  const filterDefinitions: FilterDefinition[] = useMemo(() => {
    return [
      {
        id: 'event_account',
        name: i18n.t('Event:eventAccount'),
        renderFilter: () => (
          <Select
            value={selectedEventAccountId || 'all'}
            onValueChange={(value) => {
              setSelectedEventAccountId(value === 'all' ? null : value);
              setCurrentPage(1); // Reset to first page when filter changes
            }}
          >
            <SelectTrigger className="tw:relative tw:w-full tw:bg-background">
              <ListFilterIcon aria-hidden="true" />
              <SelectValue placeholder={i18n.t('Event:allEventAccounts')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">
                {i18n.t('Event:allEventAccounts')}
              </SelectItem>
              {eventAccounts.map((account) => (
                <SelectItem key={account.id} value={account.id}>
                  {account.name || i18n.t('Event:unnamed')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ),
        renderChip: () => null,
        isActive: () => selectedEventAccountId !== null,
        onClear: () => {
          setSelectedEventAccountId(null);
          setCurrentPage(1);
        },
      },
      {
        id: 'date_range',
        name: i18n.t('Event:dateRange'),
        renderFilter: () => {
          return (
            <Popover modal={false}>
              <PopoverTrigger asChild>
                <Button variant="outline">
                  <CalendarIcon className="tw:size-4" />
                  {dateRange?.from
                    ? `${dateRange.from.toLocaleDateString()} - ${dateRange.to?.toLocaleDateString()}`
                    : i18n.t('Event:dateRange')}
                  <ChevronDownIcon className="tw:size-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                collisionPadding={16}
                className="tw:w-auto tw:overflow-hidden tw:p-0"
              >
                <Calendar
                  mode="range"
                  defaultMonth={dateRange?.from}
                  selected={dateRange}
                  onSelect={(range) => {
                    setDateRange(range);
                    setCurrentPage(1); // Reset to first page when filter changes
                  }}
                  numberOfMonths={isMobile ? 1 : 2}
                />
              </PopoverContent>
            </Popover>
          );
        },
        renderChip: () => null,
        isActive: () => dateRange?.from !== undefined,
        onClear: () => {
          setDateRange(undefined);
          setCurrentPage(1);
        },
      },
    ];
  }, [
    selectedEventAccountId,
    dateRange,
    eventAccounts,
    selectedEventAccount,
    isMobile,
  ]);

  return (
    <div className="tw:container tw:flex tw:flex-col tw:gap-8">
      <h1 className="tw:text-[40px] tw:font-semibold tw:leading-none tw:tracking-[-0.4px] tw:text-black">
        {i18n.t('Event:fulfillmentData')}
      </h1>

      <div className="tw:flex tw:flex-col tw:gap-4">
        {/* Tabs and Actions */}
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:flex-wrap">
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value="event" className="tw:gap-1 tw:rounded-md!">
                <CalendarIcon className="tw:size-4" />
                {i18n.t('Event:event')}
              </TabsTrigger>
              <TabsTrigger
                value="cleaning_facility"
                className="tw:gap-1 tw:rounded-md!"
              >
                <Sparkles className="tw:size-4" />
                {i18n.t('Event:cleaningFacility')}
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Actions */}
          <div className="tw:flex tw:gap-2 tw:items-center">
            <Button
              variant="outline"
              size="icon"
              onClick={() => setIsFilterBarExpanded(!isFilterBarExpanded)}
            >
              <ListFilterIcon />
            </Button>
            <Button onClick={handleDownloadExcel}>
              <Download />
              {i18n.t('Event:downloadExcelAll')}
            </Button>
          </div>
        </div>

        {/* Filter Bar - Full Width */}
        <FilterBar
          filters={filterDefinitions}
          isExpanded={isFilterBarExpanded}
          onClose={() => setIsFilterBarExpanded(false)}
        />

        {/* Table */}
        <Card className="tw:p-2">
          <ContainerTypeTable
            columns={
              activeTab === 'event'
                ? [
                    {
                      key: 'image',
                      label: i18n.t('Common:image'),
                      width: '16%',
                    },
                    {
                      key: 'ordered',
                      label: i18n.t('Event:ordered'),
                      width: '21%',
                    },
                    {
                      key: 'delivered',
                      label: i18n.t('Event:delivered'),
                      width: '21%',
                    },
                    {
                      key: 'in_stock',
                      label: i18n.t('Event:inStock'),
                      width: '21%',
                    },
                    {
                      key: 'handed_out',
                      label: i18n.t('Event:handedOut'),
                      width: '21%',
                    },
                  ]
                : [
                    {
                      key: 'image',
                      label: i18n.t('Common:image'),
                      width: '16%',
                    },
                    {
                      key: 'received_dirty',
                      label: i18n.t('Event:receivedDirty'),
                      width: '21%',
                    },
                    {
                      key: 'received_sealed',
                      label: i18n.t('Event:receivedSealed'),
                      width: '21%',
                    },
                    {
                      key: 'cleaned',
                      label: i18n.t('Event:cleaned'),
                      width: 'flex-1',
                    },
                  ]
            }
            rows={paginatedEntries.map((entry) => ({
              id: entry.container_type_id,
              rowHeight: '76px',
              cardTitle: (
                <div className="tw:flex tw:gap-2 tw:items-center">
                  <ItemTypeDisplay
                    imageUrl={entry.image_url}
                    hexCode={entry.color_hex_code}
                    alt={entry.container_type_name}
                  />
                  <div className="tw:flex tw:items-center tw:h-12">
                    <span className="tw:text-xs tw:font-medium tw:leading-[1.5] tw:text-neutral-900 tw:tracking-[0.18px] tw:overflow-ellipsis tw:overflow-hidden tw:text-nowrap">
                      {entry.container_type_name}
                    </span>
                  </div>
                </div>
              ),
              cells:
                activeTab === 'event'
                  ? [
                      <div
                        key="image"
                        className="tw:flex tw:items-start tw:relative tw:shrink-0"
                      >
                        <ItemTypeDisplay
                          imageUrl={entry.image_url}
                          hexCode={entry.color_hex_code}
                          alt={entry.container_type_name}
                        />
                      </div>,
                      <p
                        key="ordered"
                        className="tw:text-sm tw:font-normal tw:leading-[1.5] tw:text-neutral-900 tw:tracking-[0.07px]"
                      >
                        {formatValue(entry.ordered, entry.packing_unit)}
                      </p>,
                      <p
                        key="delivered"
                        className="tw:text-sm tw:font-normal tw:leading-[1.5] tw:text-neutral-900 tw:tracking-[0.07px]"
                      >
                        {formatValue(entry.delivered, entry.packing_unit)}
                      </p>,
                      <p
                        key="in_stock"
                        className="tw:text-sm tw:font-normal tw:leading-[1.5] tw:text-neutral-900 tw:tracking-[0.07px]"
                      >
                        {formatValue(entry.in_stock, entry.packing_unit)}
                      </p>,
                      <p
                        key="handed_out"
                        className="tw:text-sm tw:font-normal tw:leading-[1.5] tw:text-neutral-900 tw:tracking-[0.07px]"
                      >
                        {formatValue(entry.handed_out, entry.packing_unit)}
                      </p>,
                    ]
                  : [
                      <div
                        key="image"
                        className="tw:flex tw:items-start tw:relative tw:shrink-0"
                      >
                        <ItemTypeDisplay
                          imageUrl={entry.image_url}
                          hexCode={entry.color_hex_code}
                          alt={entry.container_type_name}
                        />
                      </div>,
                      <p
                        key="received_dirty"
                        className="tw:text-sm tw:font-normal tw:leading-[1.5] tw:text-neutral-900 tw:tracking-[0.07px]"
                      >
                        {formatValue(entry.received_dirty, entry.packing_unit)}
                      </p>,
                      <p
                        key="received_sealed"
                        className="tw:text-sm tw:font-normal tw:leading-[1.5] tw:text-neutral-900 tw:tracking-[0.07px]"
                      >
                        {formatValue(entry.received_sealed, entry.packing_unit)}
                      </p>,
                      <p
                        key="cleaned"
                        className="tw:text-sm tw:font-normal tw:leading-[1.5] tw:text-neutral-900 tw:tracking-[0.07px]"
                      >
                        {formatValue(entry.cleaned, entry.packing_unit)}
                      </p>,
                    ],
            }))}
            isLoading={isLoading}
            emptyMessage={i18n.t('Event:noFulfillmentData')}
            headerHeight="54px"
          />
        </Card>

        {/* Pagination */}
        <div className="tw:flex tw:justify-end">
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages || 1}
            rowsPerPage={rowsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            onRowsPerPageChange={(newRowsPerPage) => {
              setRowsPerPage(newRowsPerPage);
              setCurrentPage(1);
            }}
            disabled={isLoading}
          />
        </div>
      </div>
    </div>
  );
}
